var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo, useState } from 'react';
import { useDebounce, useList, useUpdateEffect } from 'react-use';
/** A generic hook that handles selecting conversations across various views. */
export const useSelectConversations = ({ currentPageConversationIds, onFetchConversationIdsBulk, }) => {
    // Represents selected conversations across multiple pages.
    const [selectedConversationIds, selectedConversationIdsSetters] = useList([]);
    // Represents ids of conversations eligible for bulk selection.
    const [eligibleBulkConversationIds, eligibleBulkConversationIdsSetters] = useList([]);
    // Represents the count of filtered conversations. This essentially is used by
    // the consumer in combination with eligible bulk conversations list and
    // selected conversations to determine which and how many conversations the
    // user is allowed to perform bulk actions on.
    const [totalFilteredConversations, setTotalFilteredConversations] = useState(0);
    const [asyncStatus, setAsyncStatus] = useState('uninitialized');
    const { currentPageUnselectedConversationIds, isCurrentPageAllConversationsSelected, isCurrentPageSomeConversationsSelected, } = useMemo(() => {
        const currentPageSelectedConversationIds = selectedConversationIds.filter((id) => currentPageConversationIds.includes(id));
        const currentPageUnselectedConversationIds = currentPageConversationIds.filter((id) => !currentPageSelectedConversationIds.includes(id));
        const isCurrentPageAllConversationsSelected = currentPageConversationIds.length ===
            currentPageSelectedConversationIds.length;
        const isCurrentPageSomeConversationsSelected = !!currentPageSelectedConversationIds.length &&
            currentPageConversationIds.length >
                currentPageSelectedConversationIds.length;
        return {
            currentPageUnselectedConversationIds,
            isCurrentPageAllConversationsSelected,
            isCurrentPageSomeConversationsSelected,
        };
    }, [currentPageConversationIds, selectedConversationIds]);
    /** Whether there is a selection of one or more conversations. */
    const hasSelection = selectedConversationIds.length > 0;
    /**
     * Handles adding a conversation id to the selected conversation ids.
     *
     * @param conversationId The conversation id to select.
     */
    const selectConversation = useCallback((conversationId) => selectedConversationIdsSetters.push(conversationId), [selectedConversationIdsSetters]);
    /**
     * Handles removing a conversation id from the selected conversation ids.
     *
     * @param conversationId The conversation id to deselect.
     */
    const deselectConversation = useCallback((conversationId) => selectedConversationIdsSetters.filter((id) => id !== conversationId), [selectedConversationIdsSetters]);
    /**
     * Handles add/remove of conversation id to/from selected conversation ids.
     *
     * @param conversationId The conversation id to select/deselect.
     */
    const toggleSelectConversation = useCallback((conversationId) => selectedConversationIds.includes(conversationId)
        ? deselectConversation(conversationId)
        : selectConversation(conversationId), [deselectConversation, selectConversation, selectedConversationIds]);
    /** Handles selecting all conversations on the current page. */
    const selectAllCurrentPageConversation = useCallback(() => isCurrentPageAllConversationsSelected
        ? selectedConversationIdsSetters.filter((id) => !currentPageConversationIds.includes(id))
        : selectedConversationIdsSetters.push(...currentPageUnselectedConversationIds), [
        currentPageConversationIds,
        currentPageUnselectedConversationIds,
        isCurrentPageAllConversationsSelected,
        selectedConversationIdsSetters,
    ]);
    /** Handles selecting all eligible conversations for bulk actions. */
    const selectAllEligibleConversations = useCallback(() => {
        if (eligibleBulkConversationIds.length) {
            selectedConversationIdsSetters.set(eligibleBulkConversationIds);
        }
    }, [eligibleBulkConversationIds, selectedConversationIdsSetters]);
    /** Handles clearing all selected conversations. */
    const clearSelectedConversations = useCallback(() => selectedConversationIdsSetters.clear(), [selectedConversationIdsSetters]);
    // Handles fetching the conversations bulk ids. This happens on the first
    // selection. We debounce as fetching seems to cause minor rendering delays.
    const [_, cancelFetchingBulkIds] = useDebounce(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!hasSelection) {
            return;
        }
        try {
            setAsyncStatus('pending');
            const { csids, total_conversations } = yield onFetchConversationIdsBulk();
            eligibleBulkConversationIdsSetters.set(csids);
            setTotalFilteredConversations(total_conversations);
            setAsyncStatus('fulfilled');
        }
        catch (_a) {
            setAsyncStatus('rejected');
        }
    }), hasSelection ? 300 : 0, [hasSelection]);
    /** Resets various hook related states. */
    const resetSelection = useCallback(() => {
        setAsyncStatus('uninitialized');
        cancelFetchingBulkIds();
        clearSelectedConversations();
        eligibleBulkConversationIdsSetters.clear();
        setTotalFilteredConversations(0);
    }, [
        cancelFetchingBulkIds,
        clearSelectedConversations,
        eligibleBulkConversationIdsSetters,
    ]);
    // Handles when there's no more selection.
    useUpdateEffect(() => {
        if (!hasSelection) {
            resetSelection();
        }
    }, [cancelFetchingBulkIds, clearSelectedConversations, hasSelection]);
    return {
        asyncStatus,
        eligibleBulkConversationIds,
        isCurrentPageAllConversationsSelected,
        isCurrentPageSomeConversationsSelected,
        selectedConversationIds,
        totalFilteredConversations,
        clearSelectedConversations,
        selectAllCurrentPageConversation,
        selectAllEligibleConversations,
        selectConversation,
        deselectConversation,
        toggleSelectConversation,
        resetSelection,
    };
};
